import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*

// Dětská sekce: detail dítěte, stejný vzhled jako detail hráče (kariérní souhrn + sezónní bary +
// turnaje), ale data ze sgbowling vlastních tabulek (žádná bowlingliga). Typový přepínač (klasika/
// mantinely/dvojice) - stejné pravidlo jako u dospělých: typy her se nikdy nemixují do jednoho
// průměru (viz StatsSection.kt). Sezóny přes seasonYearOf (StatsData.kt, předěl 1.7.).

private data class ChildTournamentResult(
    val name: String,
    val date: String,
    val gameType: String,
    val placement: Int?,
    val partnerNote: String?,
    val games: Int,
    val average: Double,
    val best: Int,
)

private data class ChildSeasonStat(val year: Int, val games: Int, val average: Double, val minScore: Int, val maxScore: Int) {
    val label: String get() = "$year/${(year + 1).toString().takeLast(2)}"
}

private data class ChildPlacementRow(val date: String, val roundNo: Int?, val placement: Int)

@Composable
fun ChildDetailScreen(childId: String, onBack: () -> Unit) {
    var child by remember { mutableStateOf<SgChildDto?>(null) }
    var roundById by remember { mutableStateOf<Map<String, SgChildRoundDto>>(emptyMap()) }
    var roundScoresRaw by remember { mutableStateOf<List<SgChildScoreDto>>(emptyList()) } // jen tohoto dítěte, VŠECHNY typy
    var roundPlacementsRaw by remember { mutableStateOf<List<SgChildRoundPlacementDto>>(emptyList()) }
    var tours by remember { mutableStateOf<Map<String, SgChildTournamentDto>>(emptyMap()) }
    var tourScoresRaw by remember { mutableStateOf<List<SgChildScoreDto>>(emptyList()) } // jen tohoto dítěte, VŠECHNY typy
    var tourEntries by remember { mutableStateOf<Map<String, SgChildTournamentEntryDto>>(emptyMap()) }
    var typeFilter by remember { mutableStateOf("classic") }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }

    // Data se natáhnou jednou za dítě (nefiltrovaná typem) -- přepínání typu pak jen filtruje lokálně,
    // což zároveň umožňuje předvybrat typ, ve kterém má dítě odehráno nejvíc her.
    LaunchedEffect(childId) {
        loading = true; error = null
        try {
            child = Api.fetchChildren().find { it.id == childId }

            val rounds = Api.fetchChildRounds()
            roundById = rounds.associateBy { it.id }
            val roundIds = rounds.map { it.id }
            roundScoresRaw = Api.fetchChildRoundScores(roundIds = roundIds).filter { it.childId == childId }
            roundPlacementsRaw = Api.fetchChildRoundPlacements(roundIds = roundIds).filter { it.childId == childId }

            val tourList = Api.fetchChildTournaments()
            tours = tourList.associateBy { it.id }
            val tourIds = tourList.map { it.id }
            tourScoresRaw = Api.fetchChildTournamentScores(tournamentIds = tourIds).filter { it.childId == childId }
            tourEntries = Api.fetchChildTournamentEntries(tournamentIds = tourIds).filter { it.childId == childId }
                .mapNotNull { e -> e.tournamentId?.let { it to e } }.toMap()

            // Předvyber typ, ve kterém má dítě odehráno nejvíc her (liga + turnaje dohromady).
            typeFilter = (roundScoresRaw + tourScoresRaw).groupingBy { it.gameType }.eachCount()
                .maxByOrNull { it.value }?.key ?: "classic"
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    val roundScores = remember(roundScoresRaw, typeFilter) { roundScoresRaw.filter { it.gameType == typeFilter } }
    val seasons = remember(roundScores, roundById) {
        roundScores.mapNotNull { s -> roundById[s.roundId]?.date?.let { d -> seasonYearOf(d)?.let { y -> y to s.score } } }
            .groupBy({ it.first }, { it.second })
            .map { (year, sc) -> ChildSeasonStat(year, sc.size, (sc.average() * 10).toInt() / 10.0, sc.minOrNull() ?: 0, sc.maxOrNull() ?: 0) }
            .sortedByDescending { it.year }
    }
    // Pořadí je za celé kolo (ne za typ), ale zobrazuje se jen u kol, kde dítě odehrálo vybraný typ
    // hry -- jinak by pořadí z jiného typu (např. klasika) matlo výsledky mantinelů.
    val placements = remember(roundPlacementsRaw, roundScores, roundById) {
        val roundIdsWithType = roundScores.map { it.roundId }.toSet()
        roundPlacementsRaw.filter { it.roundId in roundIdsWithType }
            .mapNotNull { p -> roundById[p.roundId]?.let { r -> ChildPlacementRow(r.date, r.roundNo, p.placement) } }
            .sortedByDescending { it.date }
    }
    val tournaments = remember(tourScoresRaw, typeFilter, tours, tourEntries) {
        tourScoresRaw.filter { it.gameType == typeFilter && it.tournamentId != null }
            .groupBy { it.tournamentId!! }
            .mapNotNull { (tid, sc) -> tours[tid]?.let { t -> Triple(t, sc, tourEntries[tid]) } }
            .map { (t, sc, entry) ->
                val avg = if (sc.isNotEmpty()) (sc.map { it.score }.average() * 10).toInt() / 10.0 else 0.0
                ChildTournamentResult(t.name, t.date, t.gameType, entry?.placement, entry?.partnerNote, sc.size, avg, sc.maxOfOrNull { it.score } ?: 0)
            }.sortedByDescending { it.date }
    }

    Button(attrs = { classes("back-button"); onClick { onBack() } }) { Text("← Zpět na děti") }

    when {
        loading -> Div(attrs = { classes("card") }) { P { Text("Načítám…") } }
        error != null -> Div(attrs = { classes("card") }) { P { Text("Chyba: $error") } }
        else -> {
            val name = child?.displayName ?: "Dítě"
            Div(attrs = { classes("card", "player-header") }) {
                val photo = child?.photoUrl
                if (!photo.isNullOrBlank()) {
                    Img(src = photo, alt = name, attrs = { classes("avatar-lg", "player-photo-lg") })
                } else {
                    Div(attrs = { classes("avatar-placeholder", "avatar-lg") }) { Text(name.take(1)) }
                }
                Div {
                    H2 { Text(name) }
                    child?.nickname?.takeIf { it.isNotBlank() }?.let { Div(attrs = { classes("medallion-nick") }) { Text("„$it“") } }
                }
            }
            child?.bioHtml?.takeIf { it.isNotBlank() }?.let {
                Div(attrs = { classes("card") }) { RichContent(it) }
            }

            Div(attrs = { classes("stat-filters") }) {
                Div(attrs = { classes("filter-group") }) {
                    Span(attrs = { classes("filter-label") }) { Text("Typ") }
                    CHILD_BOWLING_TYPES.forEach { (code, label) -> Chip(label, typeFilter == code) { typeFilter = code } }
                }
            }

            if (seasons.isNotEmpty()) {
                ChildCareerSummary(seasons)
                ChildSeasonBreakdown(seasons)
            }
            if (placements.isNotEmpty()) {
                ChildPlacementHistory(placements)
            }
            if (tournaments.isNotEmpty()) {
                ChildTournamentResults(tournaments)
            }
            if (seasons.isEmpty() && placements.isEmpty() && tournaments.isEmpty()) {
                Div(attrs = { classes("card") }) { P(attrs = { classes("muted") }) { Text("Zatím žádná data pro tento typ.") } }
            }
        }
    }
}

@Composable
private fun ChildCareerSummary(seasons: List<ChildSeasonStat>) {
    val totalGames = seasons.sumOf { it.games }
    val weightedAvg = if (totalGames > 0) (seasons.sumOf { it.average * it.games } / totalGames * 10).toInt() / 10.0 else 0.0
    val bestGame = seasons.maxOfOrNull { it.maxScore } ?: 0
    val worstGame = seasons.minOfOrNull { it.minScore } ?: 0

    Div(attrs = { classes("card") }) {
        H3 { Text("Kariérní souhrn (liga)") }
        Div(attrs = { classes("career-stats") }) {
            StatPill2("$weightedAvg", "průměr")
            StatPill2("$totalGames", "her")
            StatPill2("$bestGame", "nejlepší hra")
            StatPill2("$worstGame", "nejhorší hra")
            StatPill2("${seasons.size}", if (seasons.size == 1) "sezóna" else "sezón")
        }
    }
}

@Composable
private fun ChildSeasonBreakdown(seasons: List<ChildSeasonStat>) {
    val maxAvg = seasons.maxOfOrNull { it.average } ?: 1.0
    Div(attrs = { classes("card") }) {
        H3 { Text("Výkonnost po sezónách") }
        Div(attrs = { classes("season-list") }) {
            seasons.forEach { s ->
                Div(attrs = { classes("season-row") }) {
                    Div(attrs = { classes("season-name") }) { Text(s.label) }
                    Div(attrs = { classes("season-bar-wrap") }) {
                        val pct = if (maxAvg > 0) (s.average / maxAvg * 100).toInt() else 0
                        Div(attrs = { classes("season-bar"); attr("style", "width:${pct}%") }) { Text("Ø ${s.average}") }
                    }
                    Div(attrs = { classes("season-detail") }) { Text("${s.games} her · ${s.minScore}–${s.maxScore}") }
                }
            }
        }
    }
}

@Composable
private fun ChildPlacementHistory(placements: List<ChildPlacementRow>) {
    Div(attrs = { classes("card") }) {
        H3 { Text("Pořadí po kolech") }
        Div(attrs = { classes("training-list") }) {
            placements.forEach { p ->
                Div(attrs = { classes("training-item") }) {
                    Span(attrs = { classes("placement-chip") }) { Text("${p.placement}. místo") }
                    p.roundNo?.let { Span(attrs = { classes("muted") }) { Text(" · ${it}. kolo") } }
                    Span(attrs = { classes("muted") }) { Text(" · ${p.date}") }
                }
            }
        }
    }
}

@Composable
private fun ChildTournamentResults(results: List<ChildTournamentResult>) {
    Div(attrs = { classes("card") }) {
        H3 { Text("Turnaje") }
        Div(attrs = { classes("training-list") }) {
            results.forEach { r ->
                Div(attrs = { classes("training-item") }) {
                    Div(attrs = { classes("training-item-top") }) {
                        Div(attrs = { classes("training-head") }) {
                            r.placement?.let { Span(attrs = { classes("placement-chip") }) { Text("${it}. místo") } }
                            Span(attrs = { classes("training-date") }) { Text(" ${r.name}") }
                            Span(attrs = { classes("muted") }) { Text(" · ${r.date} · ${childBowlingTypeLabel(r.gameType)}") }
                        }
                        Div(attrs = { classes("tour-stats") }) {
                            Span(attrs = { classes("tour-avg") }) { Text("Ø ${r.average}") }
                            Span(attrs = { classes("muted") }) { Text(" ${r.games} her · max ${r.best}") }
                        }
                    }
                    r.partnerNote?.takeIf { it.isNotBlank() }?.let {
                        Div(attrs = { classes("muted") }) { Text("Parťák/mód: $it") }
                    }
                }
            }
        }
    }
}
